import { createContext, useContext, useEffect, useState } from "react";

const SettingsContext = createContext(null);

export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "pt", label: "Português" },
  { code: "tn", label: "Setswana" },
  { code: "sn", label: "chiShona" },
  { code: "nd", label: "isiNdebele" },
  { code: "st", label: "Sesotho" },
  { code: "sw", label: "Kiswahili" },
  { code: "zu", label: "isiZulu" },
  { code: "af", label: "Afrikaans" },
  { code: "xh", label: "isiXhosa" },
  { code: "yo", label: "Yorùbá" },
  { code: "ig", label: "Igbo" },
  { code: "ha", label: "Hausa" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
];

export { COUNTRIES } from "@/data/countries";
import { getCountry } from "@/data/countries";

export function SettingsProvider({ children }) {
  const [textScale, setTextScale] = useState(() => Number(localStorage.getItem("pula_text_scale")) || 1);
  const [simpleMode, setSimpleMode] = useState(() => localStorage.getItem("pula_simple") === "1");
  const [plainLanguage, setPlainLanguage] = useState(() => localStorage.getItem("pula_plain") === "1");
  const [reduceMotion, setReduceMotion] = useState(() => localStorage.getItem("pula_reduce_motion") === "1");
  const [easyRead, setEasyRead] = useState(() => localStorage.getItem("pula_easy_read") === "1");
  const [lowData, setLowData] = useState(() => localStorage.getItem("pula_low_data") === "1");
  const [language, setLanguage] = useState(() => localStorage.getItem("pula_lang") || "en");
  const [area, setArea] = useState(() => localStorage.getItem("pula_area") || "");
  const [country, setCountry] = useState(() => {
    const legacy = { UK: "GB", SA: "OTHER" };
    const stored = localStorage.getItem("pula_country") || "GB";
    return getCountry(legacy[stored] || stored).code;
  });

  useEffect(() => {
    document.documentElement.style.setProperty("--text-scale", String(textScale));
    localStorage.setItem("pula_text_scale", String(textScale));
  }, [textScale]);

  useEffect(() => {
    document.body.classList.toggle("simple-mode", simpleMode);
    localStorage.setItem("pula_simple", simpleMode ? "1" : "0");
  }, [simpleMode]);

  useEffect(() => {
    localStorage.setItem("pula_plain", plainLanguage ? "1" : "0");
  }, [plainLanguage]);

  useEffect(() => {
    document.body.classList.toggle("reduce-motion", reduceMotion);
    localStorage.setItem("pula_reduce_motion", reduceMotion ? "1" : "0");
  }, [reduceMotion]);

  useEffect(() => {
    document.body.classList.toggle("easy-read", easyRead);
    localStorage.setItem("pula_easy_read", easyRead ? "1" : "0");
  }, [easyRead]);

  useEffect(() => {
    document.body.classList.toggle("low-data", lowData);
    localStorage.setItem("pula_low_data", lowData ? "1" : "0");
    // Tell the service worker so it can skip downloading new images (real data saving).
    const tell = () => navigator.serviceWorker?.controller?.postMessage({ type: "LOW_DATA", value: lowData });
    if (navigator.serviceWorker) {
      tell();
      navigator.serviceWorker.ready.then(tell).catch(() => {});
    }
  }, [lowData]);

  useEffect(() => {
    localStorage.setItem("pula_lang", language);
    // Remember it on the account (best-effort) so server messages like the weekly email match.
    if (localStorage.getItem("pula_token")) {
      import("@/lib/api").then(({ default: api }) => api.post("/translate/my-language", { lang: language }).catch(() => {})).catch(() => {});
    }
  }, [language]);

  useEffect(() => {
    localStorage.setItem("pula_area", area);
  }, [area]);

  useEffect(() => {
    localStorage.setItem("pula_country", country);
  }, [country]);

  const bumpText = (dir) => setTextScale((s) => Math.min(2, Math.max(1, +(s + dir * 0.1).toFixed(2))));

  return (
    <SettingsContext.Provider
      value={{ textScale, setTextScale, bumpText, simpleMode, setSimpleMode,
               plainLanguage, setPlainLanguage, reduceMotion, setReduceMotion,
               easyRead, setEasyRead, lowData, setLowData, language, setLanguage, area, setArea,
               country, setCountry }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
